/**
 * Multiline Answer Utilities
 * Utility functions and default configuration for multiline-based homework questions.
 * Supports dynamic rows of mathlive or text inputs.
 */

// Default configuration for a new multiline answer
export const defaultMultilineConfig = {
  inputType: 'mathlive',        // 'mathlive' | 'text'
  minRows: 1,
  maxRows: 10,                  // 0 = unlimited
  studentCanAddRows: true,
  rowLabels: [],                // optional prefixes e.g. ["①","②","③"] — empty = no labels
  gradingMode: 'manual',       // 'manual' | 'exactSet' | 'unorderedMatch'
  caseSensitive: false,        // for text inputType
  tolerance: 0,                // for mathlive numeric comparison
  allowTextInput: false,       // mathlive mode only: lets each row be math OR text (per-row toggle)
};

// Row-level input type. When `allowTextInput` is on, each row carries `type`
// alongside `value`. Legacy untyped rows are read as 'math' in mathlive mode
// and 'text' in text mode (see `getRowType`).
export const ROW_TYPES = Object.freeze({
  MATH: 'math',
  TEXT: 'text',
});

// Generate a unique ID for rows
let idCounter = 0;
export const generateRowId = () => {
  idCounter += 1;
  return `row_${Date.now()}_${idCounter}`;
};

/**
 * Resolve a row's effective input type.
 * - Explicit `row.type` wins.
 * - Otherwise falls back to the per-question default: mathlive config → 'math',
 *   text config → 'text'. This keeps legacy rows (saved before allowTextInput)
 *   grading and rendering exactly as they did before the flag existed.
 */
export const getRowType = (row, config) => {
  if (row && row.type === ROW_TYPES.TEXT) return ROW_TYPES.TEXT;
  if (row && row.type === ROW_TYPES.MATH) return ROW_TYPES.MATH;
  return config && config.inputType === 'text' ? ROW_TYPES.TEXT : ROW_TYPES.MATH;
};

const buildEmptyRow = (config) => {
  const row = { id: generateRowId(), value: '' };
  if (config && config.inputType === 'mathlive' && config.allowTextInput) {
    row.type = ROW_TYPES.MATH;
  }
  return row;
};

/**
 * Initialize a student answer with minRows empty rows
 */
export const initializeStudentAnswer = (config) => {
  const count = Math.max(config.minRows || 1, 1);
  const rows = [];
  for (let i = 0; i < count; i++) {
    rows.push(buildEmptyRow(config));
  }
  return { rows };
};

/**
 * Hydrate a stored student answer for the current config — pads the rows
 * array up to `minRows` so an admin who later bumps the row count doesn't
 * leave previously-saved short answers stuck below the current floor.
 * Returns the same answer (same reference) when no padding is needed.
 */
export const hydrateStudentAnswer = (value, config) => {
  if (!value || !Array.isArray(value.rows) || value.rows.length === 0) {
    return initializeStudentAnswer(config);
  }
  const minRows = Math.max(config.minRows || 1, 1);
  if (value.rows.length >= minRows) return value;
  const padded = [...value.rows];
  while (padded.length < minRows) {
    padded.push(buildEmptyRow(config));
  }
  return { ...value, rows: padded };
};

/**
 * Update a single row's value
 */
export const updateStudentRow = (answer, rowId, value) => {
  return {
    ...answer,
    rows: (answer.rows || []).map(r =>
      r.id === rowId ? { ...r, value } : r
    ),
  };
};

/**
 * Add a new empty row (respects maxRows)
 */
export const addStudentRow = (answer, config) => {
  const maxRows = config.maxRows || 0;
  const currentRows = answer.rows || [];
  if (maxRows > 0 && currentRows.length >= maxRows) return answer;
  return {
    ...answer,
    rows: [...currentRows, buildEmptyRow(config)],
  };
};

/**
 * Remove a row by ID (respects minRows)
 */
export const removeStudentRow = (answer, rowId, config) => {
  const minRows = config.minRows || 1;
  const currentRows = answer.rows || [];
  if (currentRows.length <= minRows) return answer;
  return {
    ...answer,
    rows: currentRows.filter(r => r.id !== rowId),
  };
};

/**
 * Normalize a value for comparison.
 *
 * NOTE: For `mathlive` inputType this is a *text-based* normalization only —
 * it strips LaTeX whitespace (`\,`) and lowercases. It does NOT understand
 * mathematical equivalence: `\frac{1}{2}` and `0.5` will NOT compare equal
 * via this path. Numeric tolerance is handled separately in `valuesMatch`
 * (which falls back to `parseFloat`); for richer LaTeX equivalence the
 * admin should ensure both student and answer use comparable notation.
 */
export const normalizeValue = (value, inputType) => {
  if (!value) return '';
  const str = String(value).trim();
  if (inputType === 'mathlive') {
    // Remove LaTeX formatting whitespace
    return str.replace(/\\,/g, '').replace(/\s+/g, '').toLowerCase();
  }
  return str;
};

/**
 * Compare two values for equality.
 *
 * The optional 6th `rowType` overrides `inputType` for this comparison only —
 * used when `allowTextInput` is on so a single mathlive question can mix
 * math rows and text rows. When `rowType` is omitted, behavior matches the
 * pre-flag implementation (5-arg call sites are unaffected).
 */
export const valuesMatch = (studentVal, correctVal, inputType, tolerance = 0, caseSensitive = false, rowType = null) => {
  const effectiveType = rowType === ROW_TYPES.TEXT
    ? 'text'
    : rowType === ROW_TYPES.MATH
      ? 'mathlive'
      : inputType;

  if (effectiveType === 'mathlive') {
    const sNorm = normalizeValue(studentVal, 'mathlive');
    const cNorm = normalizeValue(correctVal, 'mathlive');
    if (sNorm === cNorm) return true;

    // Try numeric comparison with tolerance
    const sNum = parseFloat(sNorm);
    const cNum = parseFloat(cNorm);
    if (!isNaN(sNum) && !isNaN(cNum)) {
      return Math.abs(sNum - cNum) <= tolerance;
    }
    return false;
  }

  // Text mode
  const s = (studentVal || '').trim();
  const c = (correctVal || '').trim();
  return caseSensitive ? s === c : s.toLowerCase() === c.toLowerCase();
};

/**
 * Get the label for a row at a given index
 */
export const getRowLabel = (config, index) => {
  if (!config.rowLabels || config.rowLabels.length === 0) return null;
  return config.rowLabels[index] || null;
};
